'use client';
import { Suspense } from 'react';

import { IRepository, IUser } from '@/types';
import { IEvaluation } from '@/lib/evaluationsApi';
import EvaluationsList from '@/components/Repository/Evaluations/EvalutationsList';
import TabularFilePicker from '../TabularFilePicker';
import Tabs from '@/ui/Tabs';
import Tag from '@/ui/Tag';
import FileUploader from '../FileUploader';
import useOxenSession from '@/hooks/useOxenSession';

const EvaluationsView = ({
  evaluations,
  repository,
  user,
  tabularFileCount,
  sectionNavigationPath,
}: {
  evaluations: IEvaluation[];
  repository: IRepository;
  user: IUser;
  tabularFileCount: number;
  sectionNavigationPath: string;
}) => {
  const { authorization } = useOxenSession();

  const hasEvaluations = !!evaluations?.length!;
  const canCreateEvaluations = authorization.forRepository(repository).canCreateEvaluations();

  return (
    <Tabs defaultValue={!!hasEvaluations ? 'existingEvaluations' : 'tabularFiles'} className="tabs-root">
      <Tabs.List open>
        <Tabs.Trigger value="existingEvaluations">
          Existing evaluations{' '}
          <Tag pill small className="count-tag">
            {evaluations?.length}
          </Tag>
        </Tabs.Trigger>
        {canCreateEvaluations && (
          <>
            <Tabs.Trigger value="tabularFiles">
              Tabular files in repository{' '}
              <Tag pill small className="count-tag">
                {tabularFileCount}
              </Tag>
            </Tabs.Trigger>
            <Tabs.Trigger value="uploadFile">Upload a tabular file</Tabs.Trigger>
          </>
        )}
      </Tabs.List>
      <Tabs.Content value="existingEvaluations" open>
        <Suspense fallback={<div>Loading evaluations...</div>}>
          <EvaluationsList
            repository={repository as IRepository}
            evaluations={evaluations}
            sectionNavigationPath={sectionNavigationPath}
          />
        </Suspense>
      </Tabs.Content>
      {canCreateEvaluations && (
        <>
          <Tabs.Content value="tabularFiles">
            <TabularFilePicker repository={repository as IRepository} />
          </Tabs.Content>
          <Tabs.Content value="uploadFile">
            <FileUploader user={user as IUser} repository={repository as IRepository} />
          </Tabs.Content>
        </>
      )}
    </Tabs>
  );
};

export default EvaluationsView;
